import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { MatterArtifactAppliedTemplateRead, MatterArtifactTemplateSelectionRead } from '../schemas'

export type ArtifactTemplatesState = {
  // Keyed by matter_artifact_id / matter_artifact_version_id. The applied
  // record has no stored output path — presence lets download/save fetch a signed URL on demand.
  templateSelectionByMatterArtifactId: Partial<Record<string, string>>
  appliedTemplateByMatterArtifactVersionId: Partial<Record<string, MatterArtifactAppliedTemplateRead>>
}

const initialState: ArtifactTemplatesState = {
  templateSelectionByMatterArtifactId: {},
  appliedTemplateByMatterArtifactVersionId: {},
}

/**
 * Artifact Templates Slice
 * Holds persisted template state for matter artifacts.
 */
export const artifactTemplatesSlice = createSlice({
  name: 'artifactTemplatesState',
  initialState,
  reducers: {
    nullifyData: () => initialState,

    upsertMatterArtifactTemplateSelections: (state, action: PayloadAction<MatterArtifactTemplateSelectionRead[]>) => {
      for (const selection of action.payload) {
        state.templateSelectionByMatterArtifactId[selection.matter_artifact_id] = selection.template_user_file_id
      }
    },
    upsertMatterArtifactAppliedTemplates: (state, action: PayloadAction<MatterArtifactAppliedTemplateRead[]>) => {
      for (const applied of action.payload) {
        state.appliedTemplateByMatterArtifactVersionId[applied.matter_artifact_version_id] = applied
      }
    },
    // Conversation-scoped hydration reconcile: upsert what the detail response returned and drop
    // in-scope entries the server no longer has (a stale selection must not survive a refetch).
    // Only ids inside the given scope are touched — other conversations share these maps.
    reconcileMatterArtifactTemplateState: (
      state,
      action: PayloadAction<{
        matterArtifactIds: string[]
        matterArtifactVersionIds: string[]
        selections: MatterArtifactTemplateSelectionRead[]
        appliedTemplates: MatterArtifactAppliedTemplateRead[]
      }>
    ) => {
      const { matterArtifactIds, matterArtifactVersionIds, selections, appliedTemplates } = action.payload
      const selectedArtifactIds = new Set(selections.map((selection) => selection.matter_artifact_id))
      for (const matterArtifactId of matterArtifactIds) {
        if (!selectedArtifactIds.has(matterArtifactId)) delete state.templateSelectionByMatterArtifactId[matterArtifactId]
      }
      for (const selection of selections) {
        state.templateSelectionByMatterArtifactId[selection.matter_artifact_id] = selection.template_user_file_id
      }
      const appliedVersionIds = new Set(appliedTemplates.map((applied) => applied.matter_artifact_version_id))
      for (const matterArtifactVersionId of matterArtifactVersionIds) {
        if (!appliedVersionIds.has(matterArtifactVersionId)) delete state.appliedTemplateByMatterArtifactVersionId[matterArtifactVersionId]
      }
      for (const applied of appliedTemplates) {
        state.appliedTemplateByMatterArtifactVersionId[applied.matter_artifact_version_id] = applied
      }
    },
    setMatterArtifactTemplateSelection: (state, action: PayloadAction<{ matterArtifactId: string; templateUserFileId: string }>) => {
      state.templateSelectionByMatterArtifactId[action.payload.matterArtifactId] = action.payload.templateUserFileId
    },
    clearMatterArtifactTemplateSelection: (state, action: PayloadAction<{ matterArtifactId: string }>) => {
      delete state.templateSelectionByMatterArtifactId[action.payload.matterArtifactId]
    },
  },
})

export const ArtifactTemplatesActions = artifactTemplatesSlice.actions

export default artifactTemplatesSlice.reducer
